import axios from 'axios';
import api from './apiService';

// Use the environment variable directly
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

/**
 * Service for authentication-related operations
 */
const authService = {
  /**
   * Set the authorization header for all future requests
   * @param {string} token - JWT token
   */
  setAuthToken: (token) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  },

  /**
   * Remove the authorization header
   */
  removeAuthToken: () => {
    delete axios.defaults.headers.common['Authorization'];
  },

  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} - Response with token and user data
   */
  login: async (email, password) => {
    try {
      // First check if the user exists
      const checkUserResponse = await api.post('/auth/check-user', { email });
      
      // If user doesn't exist, throw an error immediately
      if (!checkUserResponse.data || checkUserResponse.data.exists === false) {
        throw new Error(JSON.stringify({
          code: 'ACCOUNT_NOT_FOUND',
          message: 'Account not found. Please check your email or register.',
          redirectToRegister: true
        }));
      }

      // Only proceed with login if user exists
      const response = await api.post('/auth/login', { email, password }); 

      // If we have a valid response
      if (response.data && response.data.success) {
        return response.data;
      } 
      
      throw new Error(JSON.stringify({
        code: 'INVALID_CREDENTIALS',
        message: 'Login failed. Please check your credentials.'
      }));
    } catch (error) {
      // If it's already a structured error (from our error handling), parse and throw it
      if (error.message && error.message.startsWith('{') && error.message.endsWith('}')) {
        throw JSON.parse(error.message);
      }
      
      // Handle API errors
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 401:
            throw new Error(JSON.stringify({
              code: 'INVALID_CREDENTIALS',
              message: 'Invalid credentials. Please check your email and password.'
            }));
          case 403:
            throw new Error(JSON.stringify({
              code: 'ACCOUNT_SUSPENDED',
              message: 'Your account has been suspended. Please contact support.'
            }));
          case 429:
            throw new Error(JSON.stringify({
              code: 'TOO_MANY_ATTEMPTS',
              message: 'Too many login attempts. Please try again later.'
            }));
          case 400:
            throw new Error(JSON.stringify({
              code: 'BAD_REQUEST',
              message: data?.message || 'Invalid request. Please try again.'
            }));
          default:
            throw new Error(JSON.stringify({
              code: 'LOGIN_ERROR',
              message: data?.message || 'Login failed. Please try again.'
            }));
        }
      }

      // Handle network errors
      if (error.request) {
        throw new Error(JSON.stringify({
          code: 'NETWORK_ERROR',
          message: 'Network error. Please check your connection and try again.'
        }));
      }

      // If it's already a structured error, throw it as is
      if (error.code) {
        throw new Error(JSON.stringify(error));
      }

      // Handle other errors
      throw new Error(JSON.stringify({
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unexpected error occurred. Please try again.'
      }));
    }
  },

  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise} - Response with token and user data
   */
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error(
        error || 'Registration failed. Please try again.'
      );
    }
  },

  /**
   * Get the current authenticated user's profile
   * @returns {Promise} - Response with user data
   */
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      console.error('Get profile error:', error.response?.data || error.message);
      throw new Error(
        error.response?.data?.message || 'Failed to fetch user profile.'
      );
    }
  },

  /**
   * Update current user's profile
   * @param {Object} updateData - Profile data to update
   * @param {string} [updateData.given_name] - User's given name
   * @param {string} [updateData.family_name] - User's family name
   * @param {string} [updateData.picture] - Profile picture URL
   * @param {string} [updateData.locale] - User's locale
   * @param {string} [updateData.timezone] - User's timezone
   * @param {string} [updateData.phone] - User's phone number
   * @param {string} [updateData.bio] - User's bio
   * @param {Object} [updateData.preferences] - User's preferences
   * @param {string} [updateData.website] - User's website
   * @param {string} [updateData.company] - User's company
   * @param {string} [updateData.position] - User's position
   * @param {string} [updateData.linkedin_url] - User's LinkedIn URL
   * @param {string} [updateData.twitter_url] - User's Twitter URL
   * @param {string} [updateData.github_url] - User's GitHub URL
   * @returns {Promise} - Response with updated user data
   */
  updateProfile: async (updateData) => {
    try {
      const response = await api.put('/auth/profile', updateData);
      return response.data;
    } catch (error) {
      console.error('Update profile error:', error.response?.data || error.message);
      
      // Handle API errors
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            throw new Error(JSON.stringify({
              code: 'INVALID_DATA',
              message: data?.message || 'Invalid profile data provided.'
            }));
          case 401:
            throw new Error(JSON.stringify({
              code: 'UNAUTHORIZED',
              message: 'You are not authorized to update this profile.'
            }));
          default:
            throw new Error(JSON.stringify({
              code: 'UPDATE_FAILED',
              message: data?.message || 'Failed to update profile. Please try again.'
            }));
        }
      }

      // Handle network errors
      if (error.request) {
        throw new Error(JSON.stringify({
          code: 'NETWORK_ERROR',
          message: 'Network error. Please check your connection and try again.'
        }));
      }

      throw new Error(JSON.stringify({
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unexpected error occurred. Please try again.'
      }));
    }
  },

  /**
   * Check if the current authentication token is valid
   * @returns {Promise<boolean>} - True if token is valid
   */
  validateToken: async () => {
    try {
      await api.get('/auth/validate');
      return true;
    } catch (error) {
      return false;
    }
  },

  /**
   * Verify email with token
   * @param {string} token - Verification token
   * @returns {Promise} - Response data
   */
  verifyEmail: async (token) => {
    try {
      const response = await axios.post(`${API_URL}/auth/verify-email`, { token });
      return response.data;
    } catch (error) {
      console.error('Verification error:', error);
      
      const errorMessage = error.response?.data?.message || 'Email verification failed';
      throw new Error(errorMessage);
    }
  },

  /**
   * Resend verification email
   * @param {string} email - User email
   * @returns {Promise} - Response data
   */
  resendVerificationEmail: async (email) => {
    try {
      const response = await axios.post(`${API_URL}/auth/resend-verification`, { email });
      return response.data;
    } catch (error) {
      console.error('Error resending verification:', error);
      
      const errorMessage = error.response?.data?.message || 'Failed to resend verification email';
      throw new Error(errorMessage);
    }
  },

  /**
   * Send request for password reset
   * @param {string} email - User's email address
   * @returns {Promise} - Response with success/failure
   */
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      // Handle API errors
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 404:
            throw new Error(JSON.stringify({
              code: 'EMAIL_NOT_FOUND',
              message: 'No account found with this email address.'
            }));
          case 429:
            throw new Error(JSON.stringify({
              code: 'TOO_MANY_REQUESTS',
              message: 'Too many password reset attempts. Please try again later.'
            }));
          default:
            throw new Error(JSON.stringify({
              code: 'REQUEST_FAILED',
              message: data?.message || 'Password reset request failed. Please try again.'
            }));
        }
      }

      // Handle network errors
      if (error.request) {
        throw new Error(JSON.stringify({
          code: 'NETWORK_ERROR',
          message: 'Network error. Please check your connection and try again.'
        }));
      }

      throw new Error(JSON.stringify({
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unexpected error occurred. Please try again.'
      }));
    }
  },

  /**
   * Verify if a reset token is valid
   * @param {string} token - Password reset token
   * @returns {Promise} - Response with token validity
   */
  verifyResetToken: async (token) => {
    try {
      const response = await api.get(`/auth/reset-password/${token}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(JSON.stringify({
          code: 'INVALID_TOKEN',
          message: 'This password reset link is invalid or has expired.'
        }));
      }
      throw new Error(JSON.stringify({
        code: 'VERIFICATION_FAILED',
        message: 'Could not verify reset token. Please request a new password reset link.'
      }));
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Password reset token
   * @param {string} password - New password
   * @returns {Promise} - Response with success/failure
   */
  resetPassword: async (token, password) => {
    try {
      const response = await api.post('/auth/reset-password', { token, password });
      return response.data;
    } catch (error) {
      // Handle API errors
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            throw new Error(JSON.stringify({
              code: 'INVALID_REQUEST',
              message: data?.message || 'Invalid request. Please check your information.'
            }));
          case 401:
            throw new Error(JSON.stringify({
              code: 'INVALID_TOKEN',
              message: 'This password reset link is invalid or has expired. Please request a new one.'
            }));
          default:
            throw new Error(JSON.stringify({
              code: 'RESET_FAILED',
              message: data?.message || 'Password reset failed. Please try again.'
            }));
        }
      }

      // Handle network errors
      if (error.request) {
        throw new Error(JSON.stringify({
          code: 'NETWORK_ERROR',
          message: 'Network error. Please check your connection and try again.'
        }));
      }

      throw new Error(JSON.stringify({
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unexpected error occurred. Please try again.'
      }));
    }
  },

  /**
   * Change password for authenticated user
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise} - Response with success/failure
   */
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/auth/change-password', { currentPassword, newPassword });

      return response.data;
    } catch (error) {
      // Handle API errors
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 401:
            throw new Error(JSON.stringify({
              code: 'INCORRECT_PASSWORD',
              message: 'Your current password is incorrect.'
            }));
          case 400:
            throw new Error(JSON.stringify({
              code: 'INVALID_REQUEST',
              message: data?.message || 'Invalid request. Please check your information.'
            }));
          default:
            throw new Error(JSON.stringify({
              code: 'CHANGE_FAILED',
              message: data?.message || 'Password change failed. Please try again.'
            }));
        }
      }

      // Handle network errors
      if (error.request) {
        throw new Error(JSON.stringify({
          code: 'NETWORK_ERROR',
          message: 'Network error. Please check your connection and try again.'
        }));
      }

      throw new Error(JSON.stringify({
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unexpected error occurred. Please try again.'
      }));
    }
  }
};

export default authService;